import React, { useEffect, useRef, useState } from 'react';
import BasicInfo from './components/BasicInfo';
import { FlexWrapper, LinkStyled, UpdateKeyIconBox } from '../../../theme/common_style';
import { UpdateKeyIcon } from '../../../theme/SvgIcons';
import { useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Drawer, Segmented } from 'antd';
import GeneralInfo from './components/GeneralInfo';
import EmployeeDocuments from './components/EmployeeDocuments';
import { useDispatch, useSelector } from 'react-redux';
import { updateActiveTab } from '../../../redux/employee/EmployeeSlice';
import SalaryInfo from './components/SalaryInfo';
import Remarks from './components/Remarks';
import { toast } from 'react-toastify';
import { deleteEmployeeApi, viewEmployeeApi } from '../../../redux/employee/apiRoute';
import ActivityEmployee from '../ActivityEmployee';
import { checkPermission } from '../../../utils/common_functions';
import UpdatePassword from './modals/UpdatePassword';
import { useCurrentModule } from '../../../hooks/useCurrentModule';
import { empStatus } from '../../../utils/constant';
import EmployeeLeaves from '../EmployeeLeaves';
import { RelievingButton } from '../EmployeesStyle';
import RelievingModal from './modals/RelievingModal';
import dayjs from 'dayjs';
import { updateActivityDrawer } from '../../../redux/sidebar/SidebarSlice';
import styled from '@emotion/styled';

const ViewEmployee = () => {
  const location = useLocation();
  const { name, id } = location.state || {};
  const activeTab = useSelector((state) => state?.employeeSlice?.activeTab);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const activityDrawer = useSelector((state) => state?.sidebar?.isActivityDrawer);
  const { permissions, user_details } = useSelector((state) => state?.userInfo?.data);
  let permissionSection = useCurrentModule();
  const canDelete = checkPermission(permissionSection, 'del', permissions);
  const canUpdate = checkPermission(permissionSection, 'update', permissions);
  const [passwordModal, setPasswordModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const wrapperRef = useRef(null);

  const handleDelete = async (payload) => {
    try {
      setDeleteLoading(true);
      let res = await deleteEmployeeApi(id, payload);
      if (res?.statusCode === 200) {
        toast.success(res?.message || 'Employee deleted successfully');
        setDeleteModal(false);
        navigate('/employee');
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    } catch (error) {
      toast.error(error?.message || 'Something went wrong');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleGetEmployeeDetails = async () => {
    try {
      setLoading(true);
      let res = await viewEmployeeApi(id);
      if (res?.statusCode === 200) {
        setDetails(res?.data);
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    } catch (err) {
      toast.error(err?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) {
      navigate('/employee', { replace: true });
    } else if (activeTab === 'General Info') {
      handleGetEmployeeDetails();
      wrapperRef.current?.scrollIntoView();
    }
  }, [activeTab]);

  const breadcrumbArr = [
    {
      title: <LinkStyled to="/employee">Employees</LinkStyled>
    },
    {
      title: name
    }
  ];

  return (
    <div ref={wrapperRef}>
      {passwordModal && (
        <UpdatePassword open={passwordModal} onClose={() => setPasswordModal(false)} />
      )}
      {activityDrawer && (
        <Drawer
          width={490}
          title="Activity"
          placement="right"
          closable={true}
          prefixCls="activityCustomDrawer"
          onClose={() => dispatch(updateActivityDrawer(false))}
          open={activityDrawer}
          key="right">
          <ActivityEmployee employeeId={id} />
        </Drawer>
      )}
      {deleteModal && (
        <RelievingModal
          open={deleteModal}
          onClose={() => setDeleteModal(false)}
          handleSubmit={handleDelete}
          loading={deleteLoading}
          details={details}
        />
      )}
      <FlexWrapper justify="space-between">
        <Breadcrumb items={breadcrumbArr} />
      </FlexWrapper>
      <FlexWrapper justify="space-between" style={{ marginTop: '13px' }}>
        <Segmented
          prefixCls="antCustomSegmented"
          value={activeTab}
          options={['General Info', 'Documents', 'Salary', 'Leaves', 'Remarks']}
          onChange={(value) => {
            dispatch(updateActiveTab(value));
          }}
          className="responsive-segmented"
        />
        <FlexWrapperBox justify={'end'} gap="10px" align="center">
          {canDelete && details?.status && (
            <RelievingButton
              status={details?.status === empStatus?.DELETED}
              onClick={() => {
                if (canDelete && user_details?.id !== id) {
                  setDeleteModal(true);
                }
              }}>
              {details?.status === empStatus?.DELETED && details?.relieving_date
                ? 'Relieved on: ' + dayjs(details?.relieving_date).format('DD MMM, YYYY')
                : ' Relieved'}
            </RelievingButton>
          )}
          {canUpdate && details?.status && (
            <UpdateKeyIconBox
              canUpdate={canUpdate}
              onClick={() => {
                if (canUpdate) {
                  setPasswordModal(true);
                }
              }}>
              <UpdateKeyIcon />
            </UpdateKeyIconBox>
          )}
        </FlexWrapperBox>
      </FlexWrapper>
      {id && (
        <>
          {activeTab === 'General Info' && (
            <BasicInfo loading={loading} details={details} handleList={handleGetEmployeeDetails} />
          )}
          {activeTab === 'General Info' && (
            <GeneralInfo
              loading={loading}
              details={details}
              handleList={handleGetEmployeeDetails}
            />
          )}
          {activeTab === 'Documents' && <EmployeeDocuments />}
          {activeTab === 'Salary' && <SalaryInfo />}
          {activeTab === 'Remarks' && <Remarks />}
          {activeTab === 'Leaves' && <EmployeeLeaves />}
        </>
      )}
    </div>
  );
};

export default ViewEmployee;
const FlexWrapperBox = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  align-items: center;
  @media (max-width: 668px) {
    width: 100%;
    justify-content: end;
    align-items: end;
    margin-top: 10px;
  }
`;
